import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi, reactionRoleApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, MousePointerClick, Plus, X, AlertCircle, CheckCircle2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export function SelfAssignRolesPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const selfAssignRolesPlugin = plugins?.find((p: any) => p.pluginId === 'self-assign-roles');
  const isEnabled = selfAssignRolesPlugin?.enabled ?? false;

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  // State for reaction roles form
  const [showForm, setShowForm] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [messageId, setMessageId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [reactionsText, setReactionsText] = useState('');
  const [validationError, setValidationError] = useState('');
  const [reactionRoles, setReactionRoles] = useState<Array<any>>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const fetchReactionRoles = async () => {
    setLoadingRoles(true);
    try {
      const rows = await reactionRoleApi.getReactionRoles(serverId);
      setReactionRoles(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error fetching reaction roles:', error);
      setReactionRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchReactionRoles();
  }, [serverId, isEnabled]);

  // Toggle plugin enabled/disabled
  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'self-assign-roles') : pluginApi.disablePlugin(serverId, 'self-assign-roles'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  // Add a new reaction role
  const { mutate: createReactionRole, isPending: creating } = useApiMutation(
    (data: any) => reactionRoleApi.createReactionRole(serverId, data),
    {
      onSuccess: () => {
        toast.success('Reaction role created!');
        fetchReactionRoles();
        resetForm();
      },
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  // Update an existing reaction role
  const { mutate: updateReactionRole, isPending: updating } = useApiMutation(
    ({ id, data }: { id: string; data: any }) => reactionRoleApi.updateReactionRole(serverId, id, data),
    {
      onSuccess: () => {
        toast.success('Reaction role updated!');
        fetchReactionRoles();
        resetForm();
      },
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  // Delete a reaction role
  const { mutate: deleteReactionRole, isPending: deleting } = useApiMutation(
    (roleId: string) => reactionRoleApi.deleteReactionRole(serverId, roleId),
    {
      onSuccess: () => {
        toast.success('Reaction role deleted!');
        fetchReactionRoles();
      },
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  // Parse and validate reaction roles format
  const parseReactions = (text: string): { [key: string]: string } | null => {
    const lines = text.split('\n').filter(line => line.trim());
    const reactions: { [key: string]: string } = {};
    
    for (const line of lines) {
      const match = line.match(/^(.+?)\s*=\s*(\d+)$/);
      if (!match) {
        setValidationError(`Invalid format on line: "${line}". Use format: emoji = roleId`);
        return null;
      }
      const emoji = match[1].trim();
      const roleId = match[2].trim();
      // Discord role IDs are snowflakes (numbers only)
      if (!/^\d+$/.test(roleId)) {
        setValidationError(`Role ID must be a valid Discord ID (numbers only) on line: "${line}"`);
        return null;
      }
      reactions[emoji] = roleId;
    }
    return Object.keys(reactions).length > 0 ? reactions : null;
  };

  const validateForm = (): boolean => {
    setValidationError('');
    
    if (!messageId.trim()) {
      setValidationError('Message ID is required');
      return false;
    }
    if (!/^\d+$/.test(messageId.trim())) {
      setValidationError('Message ID must be a valid Discord ID (numbers only)');
      return false;
    }
    
    if (!channelId.trim()) {
      setValidationError('Channel is required');
      return false;
    }
    
    if (!reactionsText.trim()) {
      setValidationError('At least one reaction is required');
      return false;
    }
    
    const parsed = parseReactions(reactionsText);
    if (!parsed) {
      return false;
    }
    
    return true;
  };

  const handleSubmitReactionRole = () => {
    if (!validateForm()) return;

    const reactions = parseReactions(reactionsText);
    if (!reactions) return;

    const payload = {
      messageId: messageId.trim(),
      channelId: channelId.trim(),
      reactions,
      enabled: true,
    };

    if (editingRoleId) {
      updateReactionRole({ id: editingRoleId, data: payload });
      return;
    }

    createReactionRole(payload);
  };

  const handleEditReactionRole = (reactionRole: any) => {
    setEditingRoleId(reactionRole.id);
    setShowForm(true);
    setMessageId(reactionRole.messageId || '');
    setChannelId(reactionRole.channelId || '');

    const entries = Array.isArray(reactionRole.reactions)
      ? reactionRole.reactions
      : reactionRole.reactions && typeof reactionRole.reactions === 'object'
        ? Object.entries(reactionRole.reactions).map(([emoji, roleId]) => ({ emoji, roleId }))
        : [];

    const text = entries
      .map((entry: any) => `${entry.emoji} = ${entry.roleId}`)
      .join('\n');

    setReactionsText(text);
    setValidationError('');
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingRoleId(null);
    setMessageId('');
    setChannelId('');
    setReactionsText('');
    setValidationError('');
  };

  const channelNameById = new Map((channels as any[]).map((channel: any) => [channel.id, channel.name]));

  if (!selfAssignRolesPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-500/20 rounded-xl">
            <MousePointerClick className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Self-Assign Roles</h3>
            <p className="text-sm text-muted-foreground">
              Let members assign roles with button clicks.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 bg-teal-500/5 border-teal-500/20">
            <div className="flex gap-3">
              <MousePointerClick className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-teal-400 mb-1">How to use:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Create reaction roles with emoji reactions</li>
                  <li>Members click reactions to assign themselves roles</li>
                  <li>Supports custom emoji and Unicode emoji</li>
                  <li>Multiple reactions per message supported</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Reaction Roles List */}
          {loadingRoles ? (
            <div className="glass-card p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading reaction roles...
            </div>
          ) : reactionRoles.length > 0 && (
            <div className="glass-card p-6 space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Active Reaction Roles
              </h4>
              <div className="space-y-3">
                {reactionRoles.map((rr: any) => (
                  <div
                    key={rr.id}
                    className="p-4 bg-white/5 rounded-lg border border-white/10 flex justify-between items-start hover:bg-white/10 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-mono text-teal-400 mb-2">
                        Message: {rr.messageId} • Channel: {channelNameById.get(rr.channelId) ? `#${channelNameById.get(rr.channelId)}` : rr.channelId}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(Array.isArray(rr.reactions)
                          ? rr.reactions.map((entry: any) => [entry.emoji, entry.roleId])
                          : Object.entries(rr.reactions || {})
                        ).map(([emoji, roleId]: [string, any]) => (
                          <span
                            key={emoji}
                            className="px-3 py-1 bg-teal-500/20 border border-teal-500/30 rounded-full text-sm"
                          >
                            {emoji} → <span className="font-mono text-teal-400">{roleId}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditReactionRole(rr)}
                        className="text-blue-300 hover:text-blue-200 hover:bg-blue-500/20"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteReactionRole(rr.id)}
                        disabled={deleting}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Reaction Role Form */}
          <div className="glass-card p-6 space-y-4">
            {!showForm ? (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Reaction Role
              </Button>
            ) : (
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <MousePointerClick className="w-5 h-5 text-teal-400" />
                  {editingRoleId ? 'Edit Reaction Role' : 'Create Reaction Role'}
                </h4>

                {validationError && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200">{validationError}</p>
                  </div>
                )}

                {/* Message ID Input */}
                <div className="space-y-2">
                  <Label htmlFor="messageId" className="text-white">
                    Message ID
                    <span className="text-red-400 ml-1">*</span>
                  </Label>
                  <Input
                    id="messageId"
                    placeholder="e.g. 1234567890123456789"
                    value={messageId}
                    onChange={(e) => setMessageId(e.target.value)}
                    className="bg-white/10 border-white/20"
                  />
                  <p className="text-xs text-muted-foreground">
                    The Discord message ID where reactions will be added
                  </p>
                </div>

                {/* Channel Select */}
                <div className="space-y-2">
                  <Label htmlFor="channelId" className="text-white">
                    Channel
                    <span className="text-red-400 ml-1">*</span>
                  </Label>
                  <Select value={channelId || 'none'} onValueChange={(value) => setChannelId(value === 'none' ? '' : value)}>
                    <SelectTrigger id="channelId" className="bg-white/10 border-white/20">
                      <SelectValue placeholder="Select channel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a channel</SelectItem>
                      {(channels as any[]).map((channel: any) => (
                        <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select the Discord channel where the message is located
                  </p>
                </div>

                {/* Reactions Textarea */}
                <div className="space-y-2">
                  <Label htmlFor="reactions" className="text-white">
                    Reactions
                    <span className="text-red-400 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="reactions"
                    placeholder={"😀 = 123456789012345678\n🎉 = 987654321098765432\n🎮 = 555555555555555555"}
                    value={reactionsText}
                    onChange={(e) => setReactionsText(e.target.value)}
                    className="bg-white/10 border-white/20 font-mono text-sm min-h-[120px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Format: <code className="bg-white/10 px-1 rounded">emoji = roleId</code> (one per line)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Examples: <code className="bg-white/10 px-1 rounded">😀</code>, 
                    <code className="bg-white/10 px-1 rounded">🎉</code>, or custom emoji
                  </p>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmitReactionRole}
                    disabled={creating || updating}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white gap-2"
                  >
                    {creating || updating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {editingRoleId ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        {editingRoleId ? 'Save Changes' : 'Create'}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={resetForm}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
}
